import React from 'react';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import debounce from 'lodash/debounce';
import { Query } from '@apollo/client/react/components';
import { toGlobalId, fromGlobalId } from 'graphql-relay';
import ErrorPage from 'four-nets/errors/error-page';
import query, { QueryUsersQuery, QueryUsersQueryVariables } from './users-loader.graphql';
import { UserOrderingFilterEnum } from './users-loader.graphql.types';

import LoadingProgress from 'lib/react-router/loading-progress';
import RouteWithSearch, { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { Site } from 'four-nets/site';
import { notAllowedForVendor } from 'four-nets/vendor/permissions';

import LeftMenu from '../left-menu/left-menu';
import ProjectSelect from './project-select/project-select';
import CountySelect, { ABROAD_COUNTY } from './county-select/county-select';
import UserOrderingSelect from './user-ordering-select/user-ordering-select';
import UsersList from './users-list/users-list';

import Input from './input/input';

import defaultStyles from './users-loader.scss';

interface Search {
    county?: string;
    project?: string;
    order_by?: string;
    q?: string;
}

function toVariables(search: Search, userSearch: string) {
    /**
     * This method is here to support compability of query string params between v1 and v2, since graphql query arguments are named differenly
     */
    const variables: Partial<QueryUsersQueryVariables> = {};

    if (search.county) {
        if (search.county === ABROAD_COUNTY) {
            variables.abroad = true;
        } else {
            variables.county = toGlobalId('CountyNode', search.county);
        }
    }

    if (search.project) {
        variables.project = toGlobalId('ProjectNode', search.project);
    }

    if (search.order_by) {
        let value = search.order_by.toUpperCase();

        if (value.startsWith('-')) {
            value = `${value.replace(/^-/, '')}_DESC`;
        }

        if (UserOrderingFilterEnum[value as UserOrderingFilterEnum]) {
            variables.orderBy = value as UserOrderingFilterEnum;
        }
    } else {
        variables.orderBy = UserOrderingFilterEnum.STATS__FAVORITED_COUNT_DESC;
    }

    variables.username = userSearch;

    return variables;
}

interface Context {
    site: Site;
}

export interface Props extends RouteWithSearchRenderProps<{}, Search> {
    styles: typeof defaultStyles;
}

export interface State {
    search: string;
}

class UsersLoader extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    onUsername = debounce((search: string) => {
        this.setState({ search });
    }, 1000);

    context!: Context;

    constructor(props: Props) {
        super(props);
        this.state = { search: '' };
    }

    updateSearch = (values: object) => {
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(this.props.location.search),
                ...values,
            })}`,
        });
    };

    onCounty = (event: React.SyntheticEvent<HTMLSelectElement>) => {
        let county;

        const value = (event.target as HTMLSelectElement).value;

        if (value) {
            if (value !== ABROAD_COUNTY) {
                county = fromGlobalId(value).id;
            } else {
                county = value;
            }
        }

        this.updateSearch({ county });
    };

    onProject = (event: React.SyntheticEvent<HTMLSelectElement>) => {
        let project;

        const value = (event.target as HTMLSelectElement).value;

        if (value) {
            project = fromGlobalId(value).id;
        }

        this.updateSearch({ project });
    };

    onOrderBy = (event: React.SyntheticEvent<HTMLSelectElement>) => {
        let orderBy;

        let value = (event.target as HTMLSelectElement).value;

        if (value) {
            if (/_DESC$/.test(value)) {
                value = `-${value.replace(/_DESC$/, '')}`;
            }
            orderBy = value.toUpperCase();
        }

        this.updateSearch({ order_by: orderBy });
    };

    onSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
    };

    render() {
        const { styles, search } = this.props;

        let countryName = '';

        if (this.context.site.isSlovak) {
            countryName = 'SLOVAKIA';
        } else if (this.context.site.isCzech) {
            countryName = 'CZECH_REPUBLIC';
        }

        return (
            <ViewerConsumer>
                {({ viewer }) =>
                    !viewer ? (
                        <Unauthorized />
                    ) : (
                        <React.Fragment>
                            <FormattedMessage id='PB_MENU_FIND_USER'>
                                {msg => <h1 className={styles.title}>{msg}</h1>}
                            </FormattedMessage>
                            <div className={styles.container}>
                                <LeftMenu style={{ wrapper: styles.wrapper }} />
                                <div className={styles.users}>
                                    <Query<QueryUsersQuery, QueryUsersQueryVariables>
                                        query={query}
                                        variables={{
                                            ...toVariables(search, this.state.search),
                                            first: 10,
                                            countryName,
                                        }}
                                        fetchPolicy='cache-first'
                                    >
                                        {({ error, data, loading, fetchMore, variables }) => {
                                            return (
                                                <LoadingProgress loading={loading}>
                                                    <ErrorPage error={error}>
                                                        <React.Fragment>
                                                            <form className={styles!.form} onSubmit={this.onSubmit}>
                                                                <div className={styles!.filters}>
                                                                    {data && data.projects ? (
                                                                        <ProjectSelect
                                                                            {...data.projects}
                                                                            defaultValue={variables.project as any}
                                                                            onChange={this.onProject}
                                                                        />
                                                                    ) : null}
                                                                    {data && data.counties ? (
                                                                        <CountySelect
                                                                            {...data.counties}
                                                                            defaultValue={
                                                                                variables.abroad
                                                                                    ? ABROAD_COUNTY
                                                                                    : (variables.county as any)
                                                                            }
                                                                            onChange={this.onCounty}
                                                                        />
                                                                    ) : null}
                                                                    <FormattedMessage id='FIND_A_BRIDE_BY_NAME_OR_LOGIN'>
                                                                        {msg => (
                                                                            <Input
                                                                                style={{ wrapper: styles.username }}
                                                                                defaultValue={this.state.search}
                                                                                placeholder={msg as string}
                                                                                onChange={e =>
                                                                                    this.onUsername(e.target.value)
                                                                                }
                                                                            />
                                                                        )}
                                                                    </FormattedMessage>
                                                                </div>
                                                                {data && data.userOrdering ? (
                                                                    <UserOrderingSelect
                                                                        userOrdering={data.userOrdering}
                                                                        defaultValue={variables.orderBy as any}
                                                                        onChange={this.onOrderBy}
                                                                    />
                                                                ) : null}
                                                            </form>
                                                            {data && data.users ? (
                                                                <UsersList
                                                                    fetchMore={
                                                                        data &&
                                                                        data.users &&
                                                                        data.users.pageInfo.hasNextPage
                                                                            ? () => {
                                                                                  return fetchMore({
                                                                                      variables: {
                                                                                          ...variables,
                                                                                          cursor:
                                                                                              data &&
                                                                                              data.users &&
                                                                                              data.users.pageInfo
                                                                                                  .endCursor,
                                                                                      },
                                                                                      updateQuery: (
                                                                                          previousResult,
                                                                                          { fetchMoreResult }
                                                                                      ) => {
                                                                                          if (
                                                                                              !fetchMoreResult ||
                                                                                              !fetchMoreResult.users
                                                                                          ) {
                                                                                              return previousResult;
                                                                                          }

                                                                                          const previousEdges =
                                                                                              (previousResult.users &&
                                                                                                  previousResult.users
                                                                                                      .edges) ||
                                                                                              [];

                                                                                          return {
                                                                                              ...fetchMoreResult,
                                                                                              users: {
                                                                                                  ...fetchMoreResult.users,
                                                                                                  edges: [
                                                                                                      ...previousEdges,
                                                                                                      ...fetchMoreResult
                                                                                                          .users.edges,
                                                                                                  ],
                                                                                              },
                                                                                          };
                                                                                      },
                                                                                  });
                                                                              }
                                                                            : null
                                                                    }
                                                                    {...data.users}
                                                                />
                                                            ) : null}
                                                        </React.Fragment>
                                                    </ErrorPage>
                                                </LoadingProgress>
                                            );
                                        }}
                                    </Query>
                                </div>
                            </div>
                        </React.Fragment>
                    )
                }
            </ViewerConsumer>
        );
    }
}

const StyledUsersLoader = Styled(defaultStyles)(notAllowedForVendor(UsersLoader));
export default (props: Props) => (
    <RouteWithSearch>{renderProps => <StyledUsersLoader {...renderProps} {...props} />}</RouteWithSearch>
);
